export type AwsTelemetryDeployForm = {
  region: string;
  stackName: string;
  stackNamePrefix: string;
  clusterId: string;
  vpcId: string;
  vpcCidr: string;
  privateSubnetA: string;
  privateSubnetB: string;
  connectorInstanceA: string;
  connectorInstanceB: string;
  connectorAmiId: string;
  connectorInstanceType: string;
  connectorIamInstanceProfile: string;
  managedConnectorKeyName: string;
  managedConnectorPublicAccess: 'true' | 'false';
  managedConnectorSshCidr: string;
  connectorSecurityGroupId: string;
  existingTelemetryNlbArn: string;
  existingTelemetryNlbDnsName: string;
  existingTelemetryNlbCanonicalHostedZoneId: string;
  existingSyslogTargetGroupArn: string;
  existingSnmpTrapTargetGroupArn: string;
  existingSyslogListenerArn: string;
  existingSnmpTrapListenerArn: string;
  createConnectorIngressRules: 'true' | 'false';
  telemetryHealthCheckPort: string;
  telemetrySourceCidr: string;
  privateHostedZoneName: string;
  createPrivateHostedZone: 'true' | 'false';
  existingPrivateHostedZoneId: string;
  telemetryRecordName: string;
  createTelemetryAliasRecord: 'true' | 'false';
  createResolverEndpoint: 'true' | 'false';
  resolverAllowedCidr: string;
  existingResolverEndpointId: string;
  existingEnsureResourcesRoleArn: string;
  createLabRoutes: 'true' | 'false';
  labCidr: string;
  transitInstanceId: string;
  transitGatewayId: string;
  transitNetworkInterfaceId: string;
  labTransitPrivateIp: string;
  routeTableAId: string;
  routeTableBId: string;
};

export const createDefaultAwsTelemetryForm = (region: string): AwsTelemetryDeployForm => ({
  region,
  stackName: 'reap-telemetry',
  stackNamePrefix: 'reap',
  clusterId: '',
  vpcId: '',
  vpcCidr: '',
  privateSubnetA: '',
  privateSubnetB: '',
  connectorInstanceA: '',
  connectorInstanceB: '',
  connectorAmiId: '',
  connectorInstanceType: 't2.medium',
  connectorIamInstanceProfile: '',
  managedConnectorKeyName: '',
  managedConnectorPublicAccess: 'true',
  managedConnectorSshCidr: '0.0.0.0/0',
  connectorSecurityGroupId: '',
  existingTelemetryNlbArn: '',
  existingTelemetryNlbDnsName: '',
  existingTelemetryNlbCanonicalHostedZoneId: '',
  existingSyslogTargetGroupArn: '',
  existingSnmpTrapTargetGroupArn: '',
  existingSyslogListenerArn: '',
  existingSnmpTrapListenerArn: '',
  createConnectorIngressRules: 'true',
  telemetryHealthCheckPort: '601',
  telemetrySourceCidr: '',
  privateHostedZoneName: 'internal.reapitnow.ai',
  createPrivateHostedZone: 'true',
  existingPrivateHostedZoneId: '',
  telemetryRecordName: 'telemetry.internal.reapitnow.ai',
  createTelemetryAliasRecord: 'true',
  createResolverEndpoint: 'true',
  resolverAllowedCidr: '',
  existingResolverEndpointId: '',
  existingEnsureResourcesRoleArn: '',
  createLabRoutes: 'false',
  labCidr: '',
  transitInstanceId: '',
  transitGatewayId: '',
  transitNetworkInterfaceId: '',
  labTransitPrivateIp: '',
  routeTableAId: '',
  routeTableBId: ''
});

const parameterToFieldMap: Record<string, keyof AwsTelemetryDeployForm> = {
  StackNamePrefix: 'stackNamePrefix',
  ClusterId: 'clusterId',
  VpcId: 'vpcId',
  VpcCidr: 'vpcCidr',
  PrivateSubnetA: 'privateSubnetA',
  PrivateSubnetB: 'privateSubnetB',
  ConnectorInstanceA: 'connectorInstanceA',
  ConnectorInstanceB: 'connectorInstanceB',
  ConnectorAmiId: 'connectorAmiId',
  ConnectorInstanceType: 'connectorInstanceType',
  ConnectorIamInstanceProfile: 'connectorIamInstanceProfile',
  ManagedConnectorKeyName: 'managedConnectorKeyName',
  ManagedConnectorPublicAccess: 'managedConnectorPublicAccess',
  ManagedConnectorSshCidr: 'managedConnectorSshCidr',
  ConnectorSecurityGroupId: 'connectorSecurityGroupId',
  ExistingTelemetryNlbArn: 'existingTelemetryNlbArn',
  ExistingTelemetryNlbDnsName: 'existingTelemetryNlbDnsName',
  ExistingTelemetryNlbCanonicalHostedZoneId: 'existingTelemetryNlbCanonicalHostedZoneId',
  ExistingSyslogTargetGroupArn: 'existingSyslogTargetGroupArn',
  ExistingSnmpTrapTargetGroupArn: 'existingSnmpTrapTargetGroupArn',
  ExistingSyslogListenerArn: 'existingSyslogListenerArn',
  ExistingSnmpTrapListenerArn: 'existingSnmpTrapListenerArn',
  CreateConnectorIngressRules: 'createConnectorIngressRules',
  TelemetryHealthCheckPort: 'telemetryHealthCheckPort',
  TelemetrySourceCidr: 'telemetrySourceCidr',
  PrivateHostedZoneName: 'privateHostedZoneName',
  CreatePrivateHostedZone: 'createPrivateHostedZone',
  ExistingPrivateHostedZoneId: 'existingPrivateHostedZoneId',
  TelemetryRecordName: 'telemetryRecordName',
  CreateTelemetryAliasRecord: 'createTelemetryAliasRecord',
  CreateResolverEndpoint: 'createResolverEndpoint',
  ResolverAllowedCidr: 'resolverAllowedCidr',
  ExistingResolverEndpointId: 'existingResolverEndpointId',
  ExistingEnsureResourcesRoleArn: 'existingEnsureResourcesRoleArn',
  CreateLabRoutes: 'createLabRoutes',
  LabCidr: 'labCidr',
  TransitInstanceId: 'transitInstanceId',
  TransitGatewayId: 'transitGatewayId',
  TransitNetworkInterfaceId: 'transitNetworkInterfaceId',
  LabTransitPrivateIp: 'labTransitPrivateIp',
  RouteTableAId: 'routeTableAId',
  RouteTableBId: 'routeTableBId'
};

const flatFields = new Set<keyof AwsTelemetryDeployForm>([
  'region',
  'stackName',
  'stackNamePrefix',
  'clusterId',
  'vpcId',
  'vpcCidr',
  'privateSubnetA',
  'privateSubnetB',
  'connectorInstanceA',
  'connectorInstanceB',
  'connectorAmiId',
  'connectorInstanceType',
  'connectorIamInstanceProfile',
  'managedConnectorKeyName',
  'managedConnectorPublicAccess',
  'managedConnectorSshCidr',
  'connectorSecurityGroupId',
  'existingTelemetryNlbArn',
  'existingTelemetryNlbDnsName',
  'existingTelemetryNlbCanonicalHostedZoneId',
  'existingSyslogTargetGroupArn',
  'existingSnmpTrapTargetGroupArn',
  'existingSyslogListenerArn',
  'existingSnmpTrapListenerArn',
  'createConnectorIngressRules',
  'telemetryHealthCheckPort',
  'telemetrySourceCidr',
  'privateHostedZoneName',
  'createPrivateHostedZone',
  'existingPrivateHostedZoneId',
  'telemetryRecordName',
  'createTelemetryAliasRecord',
  'createResolverEndpoint',
  'resolverAllowedCidr',
  'existingResolverEndpointId',
  'existingEnsureResourcesRoleArn',
  'createLabRoutes',
  'labCidr',
  'transitInstanceId',
  'transitGatewayId',
  'transitNetworkInterfaceId',
  'labTransitPrivateIp',
  'routeTableAId',
  'routeTableBId'
]);

const booleanStringFields = new Set<keyof AwsTelemetryDeployForm>([
  'createConnectorIngressRules',
  'managedConnectorPublicAccess',
  'createPrivateHostedZone',
  'createTelemetryAliasRecord',
  'createResolverEndpoint',
  'createLabRoutes'
]);

const coerceFieldValue = (field: keyof AwsTelemetryDeployForm, value: unknown): AwsTelemetryDeployForm[keyof AwsTelemetryDeployForm] => {
  if (value == null) {
    return '';
  }
  if (booleanStringFields.has(field)) {
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    const normalized = String(value).trim().toLowerCase();
    return normalized === 'false' ? 'false' : 'true';
  }
  return String(value).trim();
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const applyAwsTelemetryPreset = (
  current: AwsTelemetryDeployForm,
  rawPreset: unknown
): AwsTelemetryDeployForm => {
  if (!isRecord(rawPreset)) {
    throw new Error('Preset must be a JSON object.');
  }

  const next: Record<keyof AwsTelemetryDeployForm, string> = { ...current };

  for (const field of flatFields) {
    if (field in rawPreset) {
      next[field] = coerceFieldValue(field, rawPreset[field]);
    }
  }

  const parameters = rawPreset.parameters;
  if (isRecord(parameters)) {
    for (const [parameterKey, rawValue] of Object.entries(parameters)) {
      const field = parameterToFieldMap[parameterKey];
      if (!field) {
        continue;
      }
      next[field] = coerceFieldValue(field, rawValue);
    }
  }

  return next as AwsTelemetryDeployForm;
};

export const getEffectiveTelemetryLabCidr = (
  form: Pick<AwsTelemetryDeployForm, 'labCidr' | 'telemetrySourceCidr'>
): string => form.labCidr.trim() || form.telemetrySourceCidr.trim();

export const getEffectiveTelemetryResolverAllowedCidr = (
  form: Pick<AwsTelemetryDeployForm, 'labCidr' | 'resolverAllowedCidr' | 'telemetrySourceCidr'>
): string => form.resolverAllowedCidr.trim() || getEffectiveTelemetryLabCidr(form);

export const validateAwsTelemetryIpv4Cidr = (rawValue: string, label: string): string => {
  const value = rawValue.trim();
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/.exec(value);
  if (!match) {
    throw new Error(`${label} must be a valid IPv4 CIDR, for example 10.20.0.0/27.`);
  }

  const octets = match.slice(1, 5).map(Number);
  const prefixLength = Number(match[5]);
  if (octets.some((octet) => octet < 0 || octet > 255) || prefixLength < 8 || prefixLength > 32) {
    throw new Error(`${label} must be a valid IPv4 CIDR with a /8 through /32 prefix.`);
  }

  const firstOctet = octets[0];
  if (firstOctet === 0 || firstOctet === 127 || firstOctet >= 224) {
    throw new Error(`${label} must use a routable unicast source network; ${value} is reserved.`);
  }

  const address = octets.reduce((result, octet) => result * 256 + octet, 0);
  const blockSize = 2 ** (32 - prefixLength);
  if (address % blockSize !== 0) {
    throw new Error(`${label} must use the CIDR network address, not a host address.`);
  }

  return value;
};
